import { Link } from 'react-router';
import { SEO, BreadcrumbSchema, FAQSchema, ServiceSchema, SpeakableSchema } from '@/components/seo/seo';

function RatingBadge({ rating, size = 'md' }: { rating: 'red' | 'amber' | 'green'; size?: 'sm' | 'md' | 'lg' }) {
  const colors = {
    red: 'bg-rose-500 text-white',
    amber: 'bg-amber-500 text-white',
    green: 'bg-emerald-500 text-white',
  };
  const sizes = {
    sm: 'px-2 py-0.5 text-xs',
    md: 'px-3 py-1 text-sm',
    lg: 'px-4 py-2 text-base',
  };
  return (
    <span className={`inline-block font-bold rounded-full uppercase ${colors[rating]} ${sizes[size]}`}>
      {rating}
    </span>
  );
}

function QuoteCard({ quote, source }: { quote: string; source: string }) {
  return (
    <blockquote className="bg-surface-50 border-l-4 border-brand-500 p-6 rounded-r-xl">
      <p className="text-surface-800 italic">"{quote}"</p>
      <cite className="text-sm text-surface-600 mt-2 block not-italic">— {source}</cite>
    </blockquote>
  );
}

export function NottinghamCouncilPage() {
  return (
    <div className="min-h-screen">
      <SEO
        title="Nottingham Pothole Claims | B/C Roads RED Tripled | Fixtyer"
        description="Nottingham City Council's B and C roads in RED condition tripled from 7% to 21% since 2020, and it filled 17,133 potholes in 2023/24. See what the DfT AMBER rating means for your claim."
        canonical="/councils/nottingham"
      />
      <BreadcrumbSchema
        items={[
          { name: 'Home', url: '/' },
          { name: 'Councils', url: '/councils' },
          { name: 'Nottingham', url: '/councils/nottingham' },
        ]}
      />
      <FAQSchema
        faqs={[
          {
            question: 'Nottingham earns a GREEN spend scorecard — can I still claim for pothole damage?',
            answer: 'Yes. The DfT Spend scorecard is GREEN, but your claim turns on whether the specific defect was reasonably inspected and repaired under Section 41 of the Highways Act 1980 — not on aggregate spend. Nottingham\'s own AEI condition data shows A-road RED share more than doubled (8% to 19%) and B/C-road RED share tripled (7% to 21%) between 2020 and 2024, while unclassified roads in RED rose from 12% to 14%.',
          },
          {
            question: 'What if my pothole was on a residential street in Nottingham?',
            answer: 'Unclassified roads make up 676km — 82% of Nottingham City Council\'s 825km carriageway network. The council publishes only the RED percentage for U-roads, which climbed steadily from 12% in 2020 to 14% in 2024 — roughly 95km of residential streets in the worst condition category. On a network that size, ask when your specific road was last condition-surveyed and what the AEI recorded.',
          },
          {
            question: 'Nottingham switched from SCANNER to AEI surveys in 2024 — does that weaken my claim?',
            answer: 'No — if anything it strengthens scrutiny. The council itself says it moved away from SCANNER because AEI "offered a more accurate condition, and was a truer reflection of the network, and its maintenance requirements." That is an admission the previous national data understated local need. If your incident post-dates the switch, the council chose a methodology that shows worse condition on A and B/C roads — and cannot revert to SCANNER to downplay it.',
          },
          {
            question: 'Does the drop from 17,133 to 13,881 pothole repairs mean the roads are fixed?',
            answer: 'No. Nottingham still filled 13,881 temporary pothole repairs in 2024/25 — about 38 every day — and plans 13,000 reactive/emergency pothole repairs for 2025/26. The council\'s own footnote states the 2024/25 reduction reflects increased first-time permanent repairs and planned maintenance, not a network-wide recovery. B/C roads in RED still sit at 21% under the council\'s preferred AEI methodology.',
          },
          {
            question: 'Does Nottingham\'s Streets for People funding help the council defend my claim?',
            answer: 'Not automatically. The council says Streets for People added £8.5m to highway spending since 2023 and expects further improvement from EMCCA/CRSTS2 funding. But its own condition tables show deterioration on A and B/C roads over the same period. Investment in walking and cycling routes does not prove the specific carriageway defect that damaged your vehicle was inspected, prioritised, or repaired in time.',
          },
        ]}
      />
      <ServiceSchema 
        councilName="Nottingham City Council" 
        councilSlug="nottingham" 
        rating="amber" 
      />
      <SpeakableSchema
        url="/councils/nottingham"
        headline="Nottingham City Council Road Maintenance Report"
        cssSelectors={['[data-speakable="summary"]', 'h1', 'h2']}
      />

      {/* Hero */}
      <section className="relative bg-brand-950 text-white overflow-hidden">
        {/* Top chevrons */}
        <div 
          className="absolute top-0 left-0 right-0 h-8 z-10"
          style={{
            background: `repeating-linear-gradient(
              -45deg,
              #f97316,
              #f97316 40px,
              #1e40af 40px,
              #1e40af 80px
            )`
          }}
        />
        
        <div className="max-w-5xl mx-auto px-4 sm:px-6 lg:px-8 pt-20 pb-16">
          <nav className="text-sm text-brand-400 mb-8 flex items-center gap-2">
            <Link to="/" className="hover:text-white transition-colors">Home</Link>
            <span>/</span>
            <Link to="/councils" className="hover:text-white transition-colors">Councils</Link>
            <span>/</span>
            <span className="text-white">Nottingham</span>
          </nav>

          <div className="flex flex-wrap items-center gap-3 mb-4">
            <RatingBadge rating="amber" size="lg" />
            <span className="text-brand-300">Overall</span>
            <span className="text-brand-500">|</span>
            <span className="flex items-center gap-2">
              <RatingBadge rating="amber" size="sm" /> <span className="text-brand-300 text-sm">Condition</span>
            </span>
            <span className="flex items-center gap-2">
              <RatingBadge rating="green" size="sm" /> <span className="text-brand-300 text-sm">Spend</span>
            </span>
            <span className="flex items-center gap-2">
              <RatingBadge rating="amber" size="sm" /> <span className="text-brand-300 text-sm">Best Practice</span>
            </span>
          </div>
          
          <h1 className="font-display text-4xl sm:text-5xl font-bold leading-tight mb-6">
            Nottingham: GREEN Spend, Tripling RED on Local Roads
          </h1>
          
          <p className="text-xl text-brand-200 max-w-2xl mb-8 leading-relaxed" data-speakable="summary">
            Nottingham City Council earns a GREEN DfT spend scorecard and points to £8.5m of Streets for People 
            investment. Yet the overall rating is <strong className="text-amber-400">AMBER</strong> — because 
            on the council's own preferred AEI survey, B and C roads in RED condition have 
            <strong className="text-rose-400"> tripled from 7% to 21%</strong> since 2020, A-road RED share 
            more than doubled to 19%, and the council filled 
            <strong className="text-rose-400"> 17,133 potholes in 2023/24 alone</strong> on an 
            825km network where 82% is unclassified residential streets.
          </p>

          {/* Key stat callout */}
          <div className="bg-amber-500/20 border border-amber-500/30 rounded-2xl p-6 max-w-md">
            <div className="text-6xl font-bold text-amber-400">21%</div>
            <div className="text-white font-medium mt-2">B and C roads in RED condition (2024)</div>
            <div className="text-brand-300 text-sm mt-1">
              Up from 7% in 2020 on just 67km of classified local roads — while A-road RED share 
              climbed from 8% to 19% over the same period under Nottingham's own Annual Engineering Inspection.
            </div>
          </div>
        </div>

        {/* Bottom chevrons */}
        <div 
          className="absolute bottom-0 left-0 right-0 h-8 z-10"
          style={{
            background: `repeating-linear-gradient(
              -45deg,
              #1e40af,
              #1e40af 40px,
              #f97316 40px,
              #f97316 80px
            )`
          }}
        />
      </section>

      {/* The Condition Picture */}
      <section className="py-16 bg-white">
        <div className="max-w-5xl mx-auto px-4 sm:px-6 lg:px-8">
          <h2 className="font-display text-3xl font-bold text-brand-950 mb-4">
            What The Condition Data Shows
          </h2>
          <p className="text-surface-600 mb-8">
            Five years of Annual Engineering Inspection data from Nottingham's own transparency report — 
            A-roads and B/C roads deteriorating on the council's preferred methodology
          </p>

          {/* Network context */}
          <div className="bg-surface-50 border border-surface-200 rounded-2xl p-6 mb-6">
            <h3 className="font-semibold text-brand-900 mb-4">825km network — 82% unclassified residential streets</h3>
            <div className="grid sm:grid-cols-4 gap-4 mb-4">
              <div className="text-center">
                <div className="text-2xl font-bold text-brand-900">82km</div>
                <div className="text-sm text-surface-600">A-roads (9.9%)</div>
              </div>
              <div className="text-center">
                <div className="text-2xl font-bold text-brand-900">67km</div>
                <div className="text-sm text-surface-600">B and C roads (8.1%)</div>
              </div>
              <div className="text-center">
                <div className="text-2xl font-bold text-rose-600">676km</div>
                <div className="text-sm text-surface-600">Unclassified (82%)</div>
              </div>
              <div className="text-center">
                <div className="text-2xl font-bold text-brand-900">825km</div>
                <div className="text-sm text-surface-600">Total carriageway</div>
              </div>
            </div>
            <p className="text-surface-600 text-sm">
              Nottingham City Council also manages 1,310km of footways, 206km of cycleways, 185 key bridges 
              and structures, and 37,349 road gullies — but your pothole claim will almost certainly sit on 
              one of the 676km of U-roads that dominate this network.
            </p>
          </div>

          {/* A-roads */}
          <div className="bg-rose-50 border border-rose-200 rounded-2xl p-6 mb-6">
            <h3 className="font-semibold text-rose-900 mb-4">A-roads (82km): RED share more than doubled</h3>
            <div className="bg-white rounded-xl overflow-hidden border border-rose-200 mb-4">
              <table className="w-full text-sm">
                <thead>
                  <tr className="bg-rose-100 border-b border-rose-200">
                    <th className="px-4 py-2 text-left font-semibold text-rose-900">Year</th>
                    <th className="px-4 py-2 text-center font-semibold text-rose-700">Red</th>
                    <th className="px-4 py-2 text-center font-semibold text-amber-700">Amber</th>
                    <th className="px-4 py-2 text-center font-semibold text-emerald-700">Green</th>
                  </tr>
                </thead>
                <tbody>
                  <tr className="border-b border-rose-100">
                    <td className="px-4 py-2 font-mono">2020</td>
                    <td className="px-4 py-2 text-center">8%</td>
                    <td className="px-4 py-2 text-center">47%</td>
                    <td className="px-4 py-2 text-center">45%</td>
                  </tr>
                  <tr className="border-b border-rose-100">
                    <td className="px-4 py-2 font-mono">2021</td>
                    <td className="px-4 py-2 text-center">8%</td>
                    <td className="px-4 py-2 text-center">49%</td>
                    <td className="px-4 py-2 text-center">43%</td>
                  </tr>
                  <tr className="border-b border-rose-100">
                    <td className="px-4 py-2 font-mono">2022</td>
                    <td className="px-4 py-2 text-center">10%</td>
                    <td className="px-4 py-2 text-center">47%</td>
                    <td className="px-4 py-2 text-center">43%</td>
                  </tr>
                  <tr className="border-b border-rose-100">
                    <td className="px-4 py-2 font-mono">2023</td>
                    <td className="px-4 py-2 text-center">9%</td>
                    <td className="px-4 py-2 text-center">53%</td>
                    <td className="px-4 py-2 text-center">38%</td>
                  </tr>
                  <tr className="bg-rose-50">
                    <td className="px-4 py-2 font-mono font-bold">2024</td>
                    <td className="px-4 py-2 text-center font-bold text-rose-600">19%</td>
                    <td className="px-4 py-2 text-center font-bold">31%</td>
                    <td className="px-4 py-2 text-center font-bold">51%</td>
                  </tr>
                </tbody>
              </table>
            </div>
            <p className="text-rose-800 font-medium">
              A-road RED condition jumped from 8-10% to 19% in a single year to 2024 — roughly 16km of 
              Nottingham's principal routes now in the worst category on the council's own survey.
            </p>
          </div>

          {/* B/C roads */}
          <div className="bg-amber-50 border border-amber-200 rounded-2xl p-6 mb-6">
            <h3 className="font-semibold text-amber-900 mb-4">B and C roads (67km): RED share tripled</h3>
            <div className="bg-white rounded-xl overflow-hidden border border-amber-200 mb-4">
              <table className="w-full text-sm">
                <thead>
                  <tr className="bg-amber-100 border-b border-amber-200">
                    <th className="px-4 py-2 text-left font-semibold text-amber-900">Year</th>
                    <th className="px-4 py-2 text-center font-semibold text-rose-700">Red</th>
                    <th className="px-4 py-2 text-center font-semibold text-amber-700">Amber</th>
                    <th className="px-4 py-2 text-center font-semibold text-emerald-700">Green</th>
                  </tr>
                </thead>
                <tbody>
                  <tr className="border-b border-amber-100">
                    <td className="px-4 py-2 font-mono">2020</td>
                    <td className="px-4 py-2 text-center">7%</td>
                    <td className="px-4 py-2 text-center">52%</td>
                    <td className="px-4 py-2 text-center">41%</td>
                  </tr>
                  <tr className="border-b border-amber-100">
                    <td className="px-4 py-2 font-mono">2021</td>
                    <td className="px-4 py-2 text-center">6%</td>
                    <td className="px-4 py-2 text-center">52%</td>
                    <td className="px-4 py-2 text-center">42%</td>
                  </tr>
                  <tr className="border-b border-amber-100">
                    <td className="px-4 py-2 font-mono">2022</td>
                    <td className="px-4 py-2 text-center">6%</td>
                    <td className="px-4 py-2 text-center">52%</td>
                    <td className="px-4 py-2 text-center">42%</td>
                  </tr>
                  <tr className="border-b border-amber-100">
                    <td className="px-4 py-2 font-mono">2023</td>
                    <td className="px-4 py-2 text-center">11%</td>
                    <td className="px-4 py-2 text-center">55%</td>
                    <td className="px-4 py-2 text-center">34%</td>
                  </tr>
                  <tr className="bg-amber-50">
                    <td className="px-4 py-2 font-mono font-bold">2024</td>
                    <td className="px-4 py-2 text-center font-bold text-rose-600">21%</td>
                    <td className="px-4 py-2 text-center font-bold">23%</td>
                    <td className="px-4 py-2 text-center font-bold">56%</td>
                  </tr>
                </tbody>
              </table>
            </div>
            <p className="text-amber-800 font-medium">
              B/C roads in RED climbed from 6-7% to 21% — a threefold increase. Good-condition green 
              share recovered to 56% in 2024, but that does not erase the RED tripling the council 
              itself reports on the roads connecting neighbourhoods to the wider network.
            </p>
          </div>

          {/* Spend context */}
          <div className="bg-brand-950 text-white rounded-2xl p-8">
            <h3 className="font-display text-2xl font-bold mb-4">GREEN Spend — But Condition Still AMBER</h3>
            <div className="grid sm:grid-cols-3 gap-6 mb-6">
              <div>
                <div className="text-3xl font-bold text-emerald-400">£6.2m</div>
                <div className="text-brand-300 text-sm">Projected capital spend 2025/26</div>
              </div>
              <div>
                <div className="text-3xl font-bold text-emerald-400">75%</div>
                <div className="text-brand-300 text-sm">Estimated preventative share 2025/26</div>
              </div>
              <div>
                <div className="text-3xl font-bold text-emerald-400">£8.5m</div>
                <div className="text-brand-300 text-sm">Streets for People added since 2023</div>
              </div>
            </div>
            <p className="text-xl font-semibold text-white">
              Nottingham's DfT Spend scorecard is <span className="text-emerald-400">GREEN</span> — boosted 
              by Levelling Up and CRSTS2 top-ups that took capital spend to £7.8m in 2023/24. Yet AEI 
              condition data on the council's preferred methodology shows classified roads deteriorating 
              anyway. Money is flowing; the network is still producing defects.
            </p>
          </div>
        </div>
      </section>

      {/* The U-road majority */}
      <section className="py-16 bg-surface-50">
        <div className="max-w-5xl mx-auto px-4 sm:px-6 lg:px-8">
          <h2 className="font-display text-3xl font-bold text-brand-950 mb-4">
            The 676km Residential Majority
          </h2>
          <p className="text-surface-600 mb-8">
            82% of Nottingham's carriageway network is unclassified — and RED share is still climbing
          </p>

          <div className="bg-white rounded-2xl border border-surface-200 overflow-hidden mb-8">
            <table className="w-full">
              <thead>
                <tr className="bg-surface-100 border-b border-surface-200">
                  <th className="px-6 py-3 text-left text-sm font-semibold text-brand-900">Year</th>
                  <th className="px-6 py-3 text-center text-sm font-semibold text-brand-900">U-roads in RED condition</th>
                </tr>
              </thead>
              <tbody className="text-sm">
                <tr className="border-b border-surface-100">
                  <td className="px-6 py-4 font-mono text-brand-900">2020</td>
                  <td className="px-6 py-4 text-center font-bold text-rose-600">12%</td>
                </tr>
                <tr className="border-b border-surface-100">
                  <td className="px-6 py-4 font-mono text-brand-900">2021</td>
                  <td className="px-6 py-4 text-center font-bold text-rose-600">12%</td>
                </tr>
                <tr className="border-b border-surface-100">
                  <td className="px-6 py-4 font-mono text-brand-900">2022</td>
                  <td className="px-6 py-4 text-center font-bold text-rose-600">12%</td>
                </tr>
                <tr className="border-b border-surface-100">
                  <td className="px-6 py-4 font-mono text-brand-900">2023</td>
                  <td className="px-6 py-4 text-center font-bold text-rose-600">13%</td>
                </tr>
                <tr>
                  <td className="px-6 py-4 font-mono font-bold text-brand-900">2024</td>
                  <td className="px-6 py-4 text-center font-bold text-rose-600">14%</td>
                </tr>
              </tbody>
            </table>
          </div>

          <div className="grid md:grid-cols-2 gap-6 mb-8">
            <div className="bg-white rounded-xl p-6 border border-surface-200">
              <h3 className="font-semibold text-brand-900 mb-3">~95km of Residential Roads in RED</h3>
              <p className="text-surface-600 text-sm mb-3">
                Nottingham publishes only the RED percentage for unclassified roads — not the full 
                amber/green split. At 14% RED on 676km, roughly 95km of estate streets, neighbourhood 
                routes and local connectors sit in the worst condition category the council uses.
              </p>
              <p className="text-surface-600 text-sm">
                The AEI survey covers the <strong>entire highway network</strong> annually — so the 
                council does collect data on your road. The question for Section 58 is what it did 
                with that data before your pothole formed.
              </p>
            </div>

            <div className="bg-amber-50 border border-amber-200 rounded-xl p-6">
              <h3 className="font-semibold text-amber-900 mb-3">Streets for People — Ward-by-Ward Spend</h3>
              <p className="text-amber-800 text-sm mb-3">
                The council allocated Streets for People grant funding across Nottingham's twenty wards 
                using a formula based on ward population and index of multiple deprivation statistics — 
                improving local roads for cycling and walking.
              </p>
              <p className="text-amber-800 text-sm">
                Ward-level investment is not the same as keeping every carriageway defect-free. If your 
                road was not in a Streets for People scheme, the council's neighbourhood investment 
                narrative does not help its Section 58 defence on your specific defect.
              </p>
            </div>
          </div>

          <div className="bg-brand-950 text-white rounded-2xl p-8">
            <h3 className="font-display text-xl font-bold mb-4">Why This Matters For Section 58</h3>
            <p className="text-brand-200 mb-4">
              Under Section 41 of the Highways Act 1980, Nottingham City Council must maintain its 
              highways. To rely on the Section 58 defence, it must show a reasonable inspection and 
              repair system. On a network where 82% is U-roads, ask:
            </p>
            <ul className="text-brand-300 space-y-2 mb-6">
              <li>• What did the annual AEI survey record for your road in the year before your incident?</li>
              <li>• With U-road RED share rising every year from 12% to 14%, what triggered action on yours?</li>
              <li>• Was your street in a Streets for People ward programme — or outside it entirely?</li>
              <li>• If B/C RED tripled on 67km of connector roads, what inspection priority did your residential route get?</li>
            </ul>
            <p className="text-white font-medium">
              A council that surveys the entire network annually cannot claim ignorance — only that 
              it prioritised elsewhere.
            </p>
          </div>
        </div>
      </section>

      {/* The SCANNER-to-AEI switch */}
      <section className="py-16 bg-white">
        <div className="max-w-5xl mx-auto px-4 sm:px-6 lg:px-8">
          <h2 className="font-display text-3xl font-bold text-brand-950 mb-4">
            The Methodology Switch
          </h2>
          <p className="text-surface-600 mb-8">
            In 2024 Nottingham stopped reporting SCANNER data and switched to its own Annual Engineering Inspection
          </p>

          <div className="space-y-6 mb-8">
            <QuoteCard 
              quote="In addition to this, NCC currently undertakes an Annual Engineering Inspection (AEI) condition survey on the entire highway network, and in 2024 took the decision to begin reporting the AEI condition figures and move away from SCANNER data. Given the methodology involved within the two surveys it was felt the AEI offered a more accurate condition, and was a truer reflection of the network, and its maintenance requirements."
              source="Nottingham City Council Local Highways Maintenance Transparency Report 2025"
            />
          </div>

          <div className="grid md:grid-cols-2 gap-6">
            <div className="bg-surface-50 rounded-xl p-6 border border-surface-200">
              <h3 className="font-semibold text-brand-900 mb-3">Why The Council Switched</h3>
              <p className="text-surface-600 text-sm mb-3">
                Nottingham argues SCANNER's 10m and 20m Road Condition Index intervals do not reflect 
                realistic treatment lengths for resurfacing schemes. AEI surveys are undertaken by 
                professional engineers who record an effective treatment length — which the council 
                says is more useful for capital planning.
              </p>
              <p className="text-surface-600 text-sm">
                For your claim, that is a double-edged sword: the council chose a methodology it 
                believes shows truer condition — and that methodology shows A-road RED doubling and 
                B/C RED tripling.
              </p>
            </div>
            <div className="bg-amber-50 border border-amber-200 rounded-xl p-6">
              <h3 className="font-semibold text-amber-900 mb-3">PAS 2161 From 2026/27</h3>
              <p className="text-amber-800 text-sm mb-3">
                The council notes a new five-category BSI PAS 2161 standard will apply from 2026/27, 
                replacing the current three-tier red/amber/green system. That will break the time 
                series again — much like Nottingham's own 2024 switch from SCANNER to AEI.
              </p>
              <p className="text-amber-800 text-sm">
                If your incident falls during a transition year, ask which survey standard applied 
                to your road and whether pre-2024 SCANNER data or post-2024 AEI data governs the 
                council's knowledge of its condition.
              </p>
            </div>
          </div>
        </div>
      </section>

      {/* Pothole repair conveyor */}
      <section className="py-16 bg-surface-50">
        <div className="max-w-5xl mx-auto px-4 sm:px-6 lg:px-8">
          <h2 className="font-display text-3xl font-bold text-brand-950 mb-4">
            50,051 Temporary Pothole Repairs in Five Years
          </h2>
          <p className="text-surface-600 mb-8">
            The scale of reactive repair on an 825km city network
          </p>

          <div className="bg-white rounded-2xl border border-surface-200 overflow-hidden mb-8">
            <table className="w-full">
              <thead>
                <tr className="bg-surface-100 border-b border-surface-200">
                  <th className="px-6 py-3 text-left text-sm font-semibold text-brand-900">Year</th>
                  <th className="px-6 py-3 text-center text-sm font-semibold text-brand-900">Temporary pothole repairs</th>
                </tr>
              </thead>
              <tbody className="text-sm">
                <tr className="border-b border-surface-100">
                  <td className="px-6 py-4 font-mono text-brand-900">2020/21</td>
                  <td className="px-6 py-4 text-center">5,446</td>
                </tr>
                <tr className="border-b border-surface-100">
                  <td className="px-6 py-4 font-mono text-brand-900">2021/22</td>
                  <td className="px-6 py-4 text-center">6,729</td>
                </tr>
                <tr className="border-b border-surface-100">
                  <td className="px-6 py-4 font-mono text-brand-900">2022/23</td>
                  <td className="px-6 py-4 text-center">6,862</td>
                </tr>
                <tr className="border-b border-surface-100">
                  <td className="px-6 py-4 font-mono text-brand-900">2023/24</td>
                  <td className="px-6 py-4 text-center font-bold text-rose-600">17,133</td>
                </tr>
                <tr className="border-b border-surface-100">
                  <td className="px-6 py-4 font-mono text-brand-900">2024/25</td>
                  <td className="px-6 py-4 text-center font-bold">13,881</td>
                </tr>
                <tr className="bg-brand-50">
                  <td className="px-6 py-4 font-bold text-brand-900">Five-year total</td>
                  <td className="px-6 py-4 text-center font-bold text-rose-600">50,051</td>
                </tr>
              </tbody>
            </table>
          </div>

          <div className="space-y-6 mb-8">
            <QuoteCard 
              quote="Number of potholes filled (temporary repairs) reduced as a result of increased 1st time permanent highway maintenance repairs and increased planned maintenance."
              source="Nottingham City Council Local Highways Maintenance Transparency Report 2025"
            />
          </div>

          <div className="grid md:grid-cols-2 gap-6">
            <div className="bg-white rounded-xl p-6 border border-surface-200">
              <h3 className="font-semibold text-brand-900 mb-3">The 2023/24 Spike</h3>
              <p className="text-surface-600 text-sm">
                Temporary pothole repairs more than doubled from 6,862 to 17,133 between 2022/23 and 
                2023/24 — roughly 47 every day for a full year on an 825km network. That is the 
                profile of a highway authority in reactive catch-up, not preventative control.
              </p>
            </div>
            <div className="bg-amber-50 border border-amber-200 rounded-xl p-6">
              <h3 className="font-semibold text-amber-900 mb-3">13,000 Planned for 2025/26</h3>
              <p className="text-amber-800 text-sm">
                Nottingham's 2025/26 maintenance programme still expects 13,000 reactive/emergency 
                pothole repairs — alongside 10.3km carriageway resurfacing, 7.2km surface treatments 
                and 11,120m² preventative patching. Permanent works reduce temporary counts; they 
                do not eliminate the defect pipeline.
              </p>
            </div>
          </div>
        </div>
      </section>

      {/* Spending detail */}
      <section className="py-16 bg-white">
        <div className="max-w-5xl mx-auto px-4 sm:px-6 lg:px-8">
          <h2 className="font-display text-3xl font-bold text-brand-950 mb-4">
            Where The Money Goes
          </h2>
          <p className="text-surface-600 mb-8">
            Capital and revenue spend from Nottingham's published tables — preventative share rising in 2025/26
          </p>

          <div className="bg-white rounded-2xl border border-surface-200 overflow-hidden mb-8">
            <table className="w-full text-sm">
              <thead>
                <tr className="bg-surface-100 border-b border-surface-200">
                  <th className="px-4 py-3 text-left font-semibold text-brand-900">Year</th>
                  <th className="px-4 py-3 text-center font-semibold text-brand-900">DfT capital (£,000s)</th>
                  <th className="px-4 py-3 text-center font-semibold text-brand-900">Capital spend (£,000s)</th>
                  <th className="px-4 py-3 text-center font-semibold text-brand-900">Revenue spend (£,000s)</th>
                  <th className="px-4 py-3 text-center font-semibold text-emerald-700">Preventative</th>
                  <th className="px-4 py-3 text-center font-semibold text-rose-700">Reactive</th>
                </tr>
              </thead>
              <tbody>
                <tr className="border-b border-surface-100">
                  <td className="px-4 py-3 font-mono">2020/21</td>
                  <td className="px-4 py-3 text-center">£3,970</td>
                  <td className="px-4 py-3 text-center">£3,970</td>
                  <td className="px-4 py-3 text-center">£1,671</td>
                  <td className="px-4 py-3 text-center">70%</td>
                  <td className="px-4 py-3 text-center">30%</td>
                </tr>
                <tr className="border-b border-surface-100">
                  <td className="px-4 py-3 font-mono">2021/22</td>
                  <td className="px-4 py-3 text-center">£2,765</td>
                  <td className="px-4 py-3 text-center">£2,765</td>
                  <td className="px-4 py-3 text-center">£1,671</td>
                  <td className="px-4 py-3 text-center">62%</td>
                  <td className="px-4 py-3 text-center">38%</td>
                </tr>
                <tr className="border-b border-surface-100">
                  <td className="px-4 py-3 font-mono">2022/23</td>
                  <td className="px-4 py-3 text-center">£6,256*</td>
                  <td className="px-4 py-3 text-center">£6,256*</td>
                  <td className="px-4 py-3 text-center">£1,671</td>
                  <td className="px-4 py-3 text-center">79%</td>
                  <td className="px-4 py-3 text-center">21%</td>
                </tr>
                <tr className="border-b border-surface-100">
                  <td className="px-4 py-3 font-mono">2023/24</td>
                  <td className="px-4 py-3 text-center">£7,765**</td>
                  <td className="px-4 py-3 text-center">£7,765**</td>
                  <td className="px-4 py-3 text-center">£1,971</td>
                  <td className="px-4 py-3 text-center">80%</td>
                  <td className="px-4 py-3 text-center">20%</td>
                </tr>
                <tr className="border-b border-surface-100">
                  <td className="px-4 py-3 font-mono">2024/25</td>
                  <td className="px-4 py-3 text-center">£3,090</td>
                  <td className="px-4 py-3 text-center">£3,090</td>
                  <td className="px-4 py-3 text-center">£1,993</td>
                  <td className="px-4 py-3 text-center">61%</td>
                  <td className="px-4 py-3 text-center">39%</td>
                </tr>
                <tr className="bg-brand-50">
                  <td className="px-4 py-3 font-mono font-bold">2025/26 (proj.)</td>
                  <td className="px-4 py-3 text-center font-bold">£6,173***</td>
                  <td className="px-4 py-3 text-center font-bold">£6,173***</td>
                  <td className="px-4 py-3 text-center font-bold">£2,032</td>
                  <td className="px-4 py-3 text-center font-bold text-emerald-600">75%</td>
                  <td className="px-4 py-3 text-center font-bold">25%</td>
                </tr>
              </tbody>
            </table>
          </div>

          <p className="text-surface-500 text-xs mb-8">
            *Includes £3.50m Levelling Up Funding. **Includes £5.00m Levelling Up Funding. 
            ***Includes £1.8m CRSTS2/TCR funding via EMCCA.
          </p>

          <div className="space-y-6 mb-8">
            <QuoteCard 
              quote="The SfP increased spending on the public highway by £8.5m since 2023. We expect that the network will continue to improve given recent confirmation of £1.8m of funding secured via the East Midlands Combined County Authority (EMCCA) and CRSTS 2 (now TCR) for 2025/26."
              source="Nottingham City Council Local Highways Maintenance Transparency Report 2025"
            />
          </div>

          <div className="bg-brand-950 text-white rounded-2xl p-8">
            <h3 className="font-display text-xl font-bold mb-4">2025/26 Maintenance Programme Outputs</h3>
            <div className="grid sm:grid-cols-2 md:grid-cols-3 gap-4 text-sm">
              <div><span className="text-emerald-400 font-bold">10.3km</span> <span className="text-brand-300">carriageway resurfacing</span></div>
              <div><span className="text-emerald-400 font-bold">2.2km</span> <span className="text-brand-300">in situ recycling</span></div>
              <div><span className="text-emerald-400 font-bold">7.2km</span> <span className="text-brand-300">surface treatments</span></div>
              <div><span className="text-emerald-400 font-bold">11,120m²</span> <span className="text-brand-300">preventative patching</span></div>
              <div><span className="text-emerald-400 font-bold">4.4km</span> <span className="text-brand-300">footway resurfacing</span></div>
              <div><span className="text-rose-400 font-bold">13,000</span> <span className="text-brand-300">reactive pothole repairs</span></div>
            </div>
            <p className="text-brand-200 mt-6 text-sm">
              Over 28% of the 2025/26 programme is dedicated to surface treatments and preventative 
              patching — yet the council still budgets for 13,000 emergency pothole repairs on an 
              825km network.
            </p>
          </div>
        </div>
      </section>

      {/* What AMBER + GREEN spend means for claims */}
      <section className="py-16 bg-surface-50">
        <div className="max-w-5xl mx-auto px-4 sm:px-6 lg:px-8">
          <h2 className="font-display text-3xl font-bold text-brand-950 mb-4">
            Claiming Against a GREEN-Spend AMBER Council
          </h2>
          <p className="text-surface-600 mb-8">
            Honest assessment: Nottingham invests seriously — here's how that changes your approach
          </p>

          <div className="grid md:grid-cols-2 gap-8 mb-8">
            <div className="bg-white rounded-2xl p-6 border border-surface-200">
              <h3 className="font-semibold text-brand-900 mb-4">What Works In The Council's Favour</h3>
              <ul className="text-surface-700 space-y-2 text-sm">
                <li className="flex items-start gap-2">
                  <span className="text-emerald-500">✓</span> GREEN spend scorecard — capital boosted by Levelling Up and CRSTS2 top-ups
                </li>
                <li className="flex items-start gap-2">
                  <span className="text-emerald-500">✓</span> 75% preventative spend projected for 2025/26
                </li>
                <li className="flex items-start gap-2">
                  <span className="text-emerald-500">✓</span> Full-network AEI survey annually — not an alternate-year blind spot
                </li>
                <li className="flex items-start gap-2">
                  <span className="text-emerald-500">✓</span> £8.5m Streets for People investment since 2023 across all twenty wards
                </li>
                <li className="flex items-start gap-2">
                  <span className="text-emerald-500">✓</span> Published asset management approach aligned to Well-Managed Highway Infrastructure
                </li>
              </ul>
              <p className="text-surface-500 text-sm mt-4 italic">
                Expect a documented Section 58 defence citing inspection regimes and ward investment. Generic claims will struggle.
              </p>
            </div>

            <div className="bg-amber-50 border border-amber-200 rounded-2xl p-6">
              <h3 className="font-semibold text-amber-900 mb-4">What Works In Yours</h3>
              <ul className="text-amber-800 space-y-2 text-sm">
                <li className="flex items-start gap-2">
                  <span className="text-rose-500">✗</span> AMBER condition — B/C RED tripled from 7% to 21% on the council's preferred AEI data
                </li>
                <li className="flex items-start gap-2">
                  <span className="text-rose-500">✗</span> A-road RED more than doubled to 19% in 2024
                </li>
                <li className="flex items-start gap-2">
                  <span className="text-rose-500">✗</span> U-road RED rising every year — 12% to 14%, roughly 95km of residential streets
                </li>
                <li className="flex items-start gap-2">
                  <span className="text-rose-500">✗</span> 50,051 temporary pothole repairs in five years — 17,133 in 2023/24 alone
                </li>
                <li className="flex items-start gap-2">
                  <span className="text-rose-500">✗</span> Council admits AEI shows "truer" condition than SCANNER — and chose to publish worse numbers
                </li>
              </ul>
            </div>
          </div>

          <div className="bg-brand-950 text-white rounded-2xl p-8">
            <h3 className="font-display text-xl font-bold mb-4">The Winning Strategy Here Is Specificity</h3>
            <p className="text-brand-200 mb-4">
              Against a council with GREEN spend and a full-network AEI survey, your claim lives or dies on 
              the <strong className="text-white">specific defect</strong>:
            </p>
            <ul className="text-brand-300 space-y-2 mb-6">
              <li>• Prior reports of the same pothole (FixMyStreet, council reports) — proof of actual notice</li>
              <li>• Photos showing the defect's size, depth and visible age (weathered edges, previous patching)</li>
              <li>• Whether your road was in a Streets for People ward scheme — or excluded from recent investment</li>
              <li>• The road's class — on a U-road, cite the rising 12-14% RED trend and ask what the AEI recorded for your street</li>
              <li>• Timing relative to the 2024 SCANNER-to-AEI switch — which dataset governs the council's knowledge?</li>
            </ul>
            <p className="text-white font-medium">
              Mac builds exactly this case: he searches for prior reports, assesses your photo evidence, and 
              cites Nottingham's own transparency data where it helps you.
            </p>
          </div>
        </div>
      </section>

      {/* CTA Section */}
      <section className="py-16 bg-brand-950 text-white">
        <div className="max-w-3xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <h2 className="font-display text-3xl font-bold mb-4">
            Hit a Pothole in Nottingham?
          </h2>
          <p className="text-xl text-brand-200 mb-8">
            A well-funded council demands a well-built claim. £35 for a professional claim pack.
          </p>

          <div className="grid sm:grid-cols-2 gap-6 mb-8">
            <div className="bg-brand-900/50 rounded-xl p-6 border border-brand-800">
              <h3 className="font-semibold text-brand-300 mb-3">DIY Claim</h3>
              <ul className="text-sm text-brand-400 space-y-1 text-left">
                <li>• Submit photos and invoices</li>
                <li>• Use generic template letter</li>
                <li>• No AEI methodology argument</li>
                <li>• No prior-report search</li>
                <li>• No Streets for People ward analysis</li>
              </ul>
            </div>
            <div className="bg-accent-500/20 rounded-xl p-6 border border-accent-500/30">
              <h3 className="font-semibold text-white mb-3">Professional Claim Pack</h3>
              <ul className="text-sm text-brand-200 space-y-1 text-left">
                <li>✅ B/C RED tripling documented from council AEI data</li>
                <li>✅ 17,133 repairs in 2023/24 cited</li>
                <li>✅ U-road 14% RED trend argued</li>
                <li>✅ Prior reports searched and attached</li>
                <li>✅ Section 58 rebuttal tailored to Nottingham</li>
              </ul>
            </div>
          </div>

          <div className="flex flex-col sm:flex-row gap-4 justify-center">
            <Link
              to="/check?council=nottingham"
              className="inline-flex items-center justify-center px-8 py-4 bg-accent-500 text-white font-semibold rounded-xl hover:bg-accent-600 transition-all hover:shadow-lg hover:shadow-accent-500/25"
            >
              Order My Nottingham Claim Pack — £35
              <svg className="ml-2 w-5 h-5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M17 8l4 4m0 0l-4 4m4-4H3" />
              </svg>
            </Link>
            <Link
              to="/road-maintenance-report"
              className="inline-flex items-center justify-center px-8 py-4 bg-white/10 text-white font-semibold rounded-xl hover:bg-white/20 transition-colors"
            >
              View All Council Ratings
            </Link>
          </div>

          <p className="text-brand-400 text-sm mt-6">
            No percentage fees. You keep 100% of any compensation.
          </p>
        </div>
      </section>

      {/* FAQs */}
      <section className="py-16 bg-surface-50">
        <div className="max-w-3xl mx-auto px-4 sm:px-6 lg:px-8">
          <h2 className="font-display text-2xl font-bold text-brand-950 mb-8">
            Frequently Asked Questions
          </h2>
          
          <div className="space-y-6">
            <div className="bg-white rounded-xl p-6 border border-surface-200">
              <h3 className="font-semibold text-brand-900 mb-2">
                Nottingham earns a GREEN spend scorecard — can I still claim for pothole damage?
              </h3>
              <p className="text-surface-600 text-sm">
                Yes. The DfT Spend scorecard is GREEN, but your claim turns on whether the specific defect 
                was reasonably inspected and repaired under Section 41 of the Highways Act 1980 — not on 
                aggregate spend. Nottingham's own AEI condition data shows A-road RED share more than doubled 
                (8% to 19%) and B/C-road RED share tripled (7% to 21%) between 2020 and 2024, while 
                unclassified roads in RED rose from 12% to 14%.
              </p>
            </div>

            <div className="bg-white rounded-xl p-6 border border-surface-200">
              <h3 className="font-semibold text-brand-900 mb-2">
                What if my pothole was on a residential street in Nottingham?
              </h3>
              <p className="text-surface-600 text-sm">
                Unclassified roads make up 676km — 82% of Nottingham City Council's 825km carriageway network. 
                The council publishes only the RED percentage for U-roads, which climbed steadily from 12% in 
                2020 to 14% in 2024 — roughly 95km of residential streets in the worst condition category. 
                On a network that size, ask when your specific road was last condition-surveyed and what the 
                AEI recorded.
              </p>
            </div>

            <div className="bg-white rounded-xl p-6 border border-surface-200">
              <h3 className="font-semibold text-brand-900 mb-2">
                Nottingham switched from SCANNER to AEI surveys in 2024 — does that weaken my claim?
              </h3>
              <p className="text-surface-600 text-sm">
                No — if anything it strengthens scrutiny. The council itself says it moved away from SCANNER 
                because AEI "offered a more accurate condition, and was a truer reflection of the network, 
                and its maintenance requirements." That is an admission the previous national data understated 
                local need. If your incident post-dates the switch, the council chose a methodology that shows 
                worse condition on A and B/C roads — and cannot revert to SCANNER to downplay it.
              </p>
            </div>

            <div className="bg-white rounded-xl p-6 border border-surface-200">
              <h3 className="font-semibold text-brand-900 mb-2">
                Does the drop from 17,133 to 13,881 pothole repairs mean the roads are fixed?
              </h3>
              <p className="text-surface-600 text-sm">
                No. Nottingham still filled 13,881 temporary pothole repairs in 2024/25 — about 38 every day — 
                and plans 13,000 reactive/emergency pothole repairs for 2025/26. The council's own footnote 
                states the 2024/25 reduction reflects increased first-time permanent repairs and planned 
                maintenance, not a network-wide recovery. B/C roads in RED still sit at 21% under the council's 
                preferred AEI methodology.
              </p>
            </div>

            <div className="bg-white rounded-xl p-6 border border-surface-200">
              <h3 className="font-semibold text-brand-900 mb-2">
                Does Nottingham's Streets for People funding help the council defend my claim?
              </h3>
              <p className="text-surface-600 text-sm">
                Not automatically. The council says Streets for People added £8.5m to highway spending since 
                2023 and expects further improvement from EMCCA/CRSTS2 funding. But its own condition tables 
                show deterioration on A and B/C roads over the same period. Investment in walking and cycling 
                routes does not prove the specific carriageway defect that damaged your vehicle was inspected, 
                prioritised, or repaired in time.
              </p>
            </div>
          </div>
        </div>
      </section>

      {/* Attribution Footer */}
      <section className="py-8 bg-surface-100 border-t border-surface-200">
        <div className="max-w-5xl mx-auto px-4 sm:px-6 lg:px-8">
          <p className="text-xs text-surface-500 text-center">
            Data sources: <a 
              href="https://www.gov.uk/government/publications/local-road-maintenance-ratings/local-road-maintenance-ratings-2025-to-2026"
              target="_blank"
              rel="noopener noreferrer"
              className="text-brand-600 hover:underline"
            >
              Department for Transport — Local Road Maintenance Ratings 2025 to 2026
            </a> | <a 
              href="https://www.transportnottingham.com/local-highways-maintenance-transparency-report/"
              target="_blank"
              rel="noopener noreferrer"
              className="text-brand-600 hover:underline"
            >
              Nottingham City Council Local Highways Maintenance Transparency Report (June 2025)
            </a>. 
            Contains public sector information licensed under the{' '}
            <a href="https://www.nationalarchives.gov.uk/doc/open-government-licence/version/3/" target="_blank" rel="noopener noreferrer" className="text-brand-600 hover:underline">
              Open Government Licence v3.0
            </a>.
          </p>
        </div>
      </section>
    </div>
  );
}
